import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { RejectedValue, ErrorResponse } from "shared/interfaces/core-interfaces";
import {
    TimeReportDataResponse,
    TimeReportCreateDataResponse,
    TimeReportFilterRequest,
    TimeReportExportRequest,
    initialTimeReportState,
} from "./timeReportInterfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetTimeReport = createAsyncThunk<TimeReportDataResponse, TimeReportFilterRequest, { rejectValue: RejectedValue }>(
    'Slice/TimeReportSlice/GetTimeReport',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-report/get', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            return await response.json() as TimeReportDataResponse;
        }
        const errorResponse = await response.json() as ErrorResponse;
        dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
        return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
    }
);

export const GetTimeReportData = createAsyncThunk<TimeReportCreateDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/TimeReportSlice/GetTimeReportData',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-report/get-data', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            return await response.json() as TimeReportCreateDataResponse;
        }
        const errorResponse = await response.json() as ErrorResponse;
        dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
        return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
    }
);

export const ExportTimeReportPdf = createAsyncThunk<void, { request: TimeReportExportRequest; fileName: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeReportSlice/ExportTimeReportPdf',
    async ({ request, fileName }, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-report/export/pdf', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileName}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }
        const errorResponse = await response.json() as ErrorResponse;
        dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
        return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
    }
);

export const ExportTimeReportXlsx = createAsyncThunk<void, { request: TimeReportExportRequest; fileName: string }, { rejectValue: RejectedValue }>(
    'Slice/TimeReportSlice/ExportTimeReportXlsx',
    async ({ request, fileName }, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/time-report/export/xlsx', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileName}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            return;
        }
        const errorResponse = await response.json() as ErrorResponse;
        dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
        return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
    }
);

export const TimeReportSlice = createSlice({
    name: 'TimeReportSlice',
    initialState: initialTimeReportState,
    reducers: {
        setTimeReportFilter: (state, action: PayloadAction<TimeReportFilterRequest>) => {
            state.activeFilter = action.payload;
        },
    },
    extraReducers(builder) {
        builder.addCase(GetTimeReport.fulfilled, (state, action: PayloadAction<TimeReportDataResponse>) => {
            state.timeReportData = action.payload;
        });
        builder.addCase(GetTimeReport.rejected, (state) => {
            state.timeReportData = null;
        });
        builder.addCase(GetTimeReportData.fulfilled, (state, action: PayloadAction<TimeReportCreateDataResponse>) => {
            state.createData = action.payload;
        });
    }
});

export const { setTimeReportFilter } = TimeReportSlice.actions;
export default TimeReportSlice.reducer;
